import { useQueryClient } from "@tanstack/react-query"
import { useSocket, type SocketEnabled } from "./useSocket"

type SyncQueryOptions<QueryData, Raw> = {
  isPrivate?: boolean
  eventName: string | string[]
  enabled?: SocketEnabled<Raw>
  updater: (oldData: QueryData | undefined, eventData: Raw) => QueryData | undefined
  onBeforeSync?: (eventName: string, eventData: Raw) => void
  onAfterSync?: (eventName: string, eventData: Raw, newData: QueryData) => void
}

/**
 * useSyncQueryData — subscribes to the signage private channel and keeps a
 * React-Query cache entry in sync with incoming events.
 *
 * Pass `eventName` to only react to a specific event and skip all others.
 * Pass `enabled` as a boolean to gate the connection, or as a predicate
 * `(event, data) => boolean` for fine-grained per-event filtering.
 *
 * @param queryKey             - the React-Query key to update
 * @param options
 * @param options.channel      - override resolved channel name
 * @param options.isPrivate    - use private channel (default: true)
 * @param options.eventName    - sync only when one of these event name(s) is received
 * @param options.enabled      - boolean or predicate to control syncing
 * @param options.updater      - merge incoming event data into cached query data
 * @param options.onBeforeSync - called before the query cache is updated
 * @param options.onAfterSync  - called after the query cache is updated
 */
export function useSyncQueryData<QueryData, Raw>(queryKey: unknown[], options: SyncQueryOptions<QueryData, Raw>) {
  const queryClient = useQueryClient()

  useSocket<Raw>({
    isPrivate: options.isPrivate ?? true,
    eventName: options.eventName,
    enabled: options.enabled,
    handler: (incomingEvent, eventData) => {
      const eventNames = Array.isArray(options.eventName) ? options.eventName : [options.eventName]

      if (!eventNames.includes(incomingEvent)) return

      options.onBeforeSync?.(incomingEvent, eventData)

      let updatedData: QueryData | undefined

      queryClient.setQueryData<QueryData>(queryKey, (oldData) => {
        updatedData = options.updater(oldData, eventData)

        return updatedData
      })

      if (updatedData) {
        options.onAfterSync?.(incomingEvent, eventData, updatedData)
      }
    },
  })
}
